import { useEffect, useRef, useState } from "react";
import { FileDown, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadAuth } from "@/lib/api";
import { Txt, Field, Input, rd, useColl, Stat } from "./Inline";

const ST = { pagada: "bg-emerald-50 text-emerald-700", parcial: "bg-amber-50 text-amber-700", pendiente: "bg-red-50 text-red-700" };
const today = () => new Date().toLocaleDateString("en-CA");

function Items({ inv, save }) {
  const items = inv.items || [];
  const set = (x) => save({ items: x });
  const upd = (i, p) => set(items.map((it, j) => (j === i ? { ...it, ...p } : it)));
  return (
    <div className="space-y-2" data-testid={`hinv-items-${inv.id}`}>
      <div className="hidden sm:grid grid-cols-[1fr_90px_120px_120px_auto] gap-2 text-xs uppercase font-semibold text-slate-500"><span>Descripción</span><span>Cant.</span><span>Precio RD$</span><span>Importe</span><span /></div>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_90px_120px_120px_auto] gap-2 items-center">
          <div className="col-span-2 sm:col-span-1"><Input row={it} k="description" onSave={(p) => upd(i, p)} tid={`hinv-item-desc-${inv.id}-${i}`} /></div>
          <Input row={it} k="qty" type="number" onSave={(p) => upd(i, p)} tid={`hinv-item-qty-${inv.id}-${i}`} />
          <Input row={it} k="price" type="number" onSave={(p) => upd(i, p)} tid={`hinv-item-price-${inv.id}-${i}`} />
          <span className="font-mono-num text-sm">{rd((it.qty || 0) * (it.price || 0))}</span>
          <button onClick={() => set(items.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-600 justify-self-start"><X size={15} /></button>
        </div>
      ))}
      <button onClick={() => set([...items, { description: "", qty: 1, price: 0 }])} className="text-sm font-semibold text-[#FF3333] inline-flex items-center gap-1" data-testid={`hinv-item-add-${inv.id}`}><Plus size={14} />Agregar línea</button>
    </div>
  );
}

function InvoiceCard({ inv, save, del }) {
  const s = (p) => save(inv.id, p);
  const P = { row: inv, onSave: s, prefix: "hinv" };
  return (
    <article className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid={`hinv-card-${inv.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3"><h3 className="font-bold text-lg">{inv.number}</h3><span className={`text-xs font-semibold rounded-full px-2.5 py-1 capitalize ${ST[inv.status]}`} data-testid={`hinv-status-${inv.id}`}>{inv.status}</span>{inv.job_name && <span className="text-sm text-slate-500">{inv.job_name}</span>}</div>
        <div className="flex gap-2">
          <button onClick={() => downloadAuth(`/admin/heavy/invoices/${inv.id}/pdf`, `factura_${inv.number}.pdf`)} className="h-9 rounded-full border border-slate-300 px-4 text-sm font-semibold inline-flex items-center gap-2 hover:border-[#FF3333]" data-testid={`hinv-pdf-${inv.id}`}><FileDown size={15} />PDF</button>
          <button onClick={() => del(inv.id, `la factura ${inv.number}`)} className="h-9 w-9 rounded-full grid place-items-center text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`hinv-delete-${inv.id}`}><Trash2 size={15} /></button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Txt {...P} k="date" label="Fecha" type="date" /><Txt {...P} k="due_date" label="Vence" type="date" />
        <Txt {...P} k="client_name" label="Cliente" /><Txt {...P} k="client_rnc" label="RNC / Cédula" /><Txt {...P} k="client_phone" label="Teléfono" type="tel" /><Txt {...P} k="client_email" label="Email" type="email" />
      </div>
      <Items inv={inv} save={s} />
      <div className="flex flex-wrap items-end justify-between gap-4 border-t pt-4">
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!inv.itbis} onChange={(e) => s({ itbis: e.target.checked })} data-testid={`hinv-itbis-${inv.id}`} />Cobrar ITBIS 18%</label>
        <div className="w-40"><Field label="Pagado RD$"><Input row={inv} k="paid_amount" type="number" onSave={s} tid={`hinv-paid-${inv.id}`} /></Field></div>
        <div className="text-sm text-right space-y-0.5 font-mono-num">
          <p>Subtotal: {rd(inv.subtotal)}</p>{inv.itbis && <p>ITBIS: {rd(inv.tax)}</p>}
          <p className="font-bold text-base" data-testid={`hinv-total-${inv.id}`}>Total: {rd(inv.total)}</p>
          <p className={inv.balance > 0 ? "text-red-600 font-semibold" : "text-emerald-700"} data-testid={`hinv-balance-${inv.id}`}>Por cobrar: {rd(inv.balance)}</p>
        </div>
      </div>
    </article>
  );
}

export function InvoicesTab() {
  const c = useColl("heavy_invoices", { sort: "-date" });
  const [f, setF] = useState("todas");
  const rows = c.rows.filter((r) => f === "todas" || (f === "por_cobrar" ? r.balance > 0 : r.status === "pagada"));
  const sum = (k, list) => list.reduce((a, r) => a + (r[k] || 0), 0);
  return (
    <div className="space-y-4" data-testid="hinv">
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
        <Stat l="Facturado" v={rd(sum("total", c.rows))} tid="hinv-sum-total" /><Stat l="Cobrado" v={rd(sum("paid_amount", c.rows))} tid="hinv-sum-paid" />
        <Stat l="Cuentas por cobrar" v={rd(sum("balance", c.rows.filter((r) => r.balance > 0)))} tid="hinv-sum-balance" tone="text-red-600" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">{[["todas", "Todas"], ["por_cobrar", "Por cobrar"], ["pagadas", "Pagadas"]].map(([k, l]) => (
          <button key={k} onClick={() => setF(k)} className={`h-9 rounded-full px-4 text-sm font-semibold ${f === k ? "bg-[#121316] text-white" : "bg-white border border-slate-200"}`} data-testid={`hinv-filter-${k}`}>{l}</button>))}</div>
        <button onClick={() => c.add({ date: today(), due_date: today(), items: [{ description: "", qty: 1, price: 0 }] })} className="h-10 rounded-full bg-[#FF3333] hover:bg-[#E62E2E] text-white px-5 text-sm font-semibold inline-flex items-center gap-2" data-testid="hinv-add-btn"><Plus size={15} />Factura manual</button>
      </div>
      <p className="text-sm text-slate-500">Para facturar las horas de un trabajo, abre el trabajo y pulsa "Crear factura".</p>
      {rows.map((r) => <InvoiceCard key={r.id} inv={r} save={c.save} del={c.del} />)}
      {!rows.length && <p className="text-center text-slate-400 py-10 bg-white rounded-2xl border border-dashed" data-testid="hinv-empty">No hay facturas.</p>}
    </div>
  );
}

export function CompanyTab() {
  const [d, setD] = useState(null);
  const chain = useRef(Promise.resolve());
  const pending = useRef(0);
  useEffect(() => { api.get("/admin/c/heavy_company").then((r) => setD(r.data[0] || {})); }, []);
  const save = (p) => {
    setD((x) => ({ ...x, ...p }));
    pending.current += 1;
    chain.current = chain.current.then(async () => {
      try {
        const id = (await api.get("/admin/c/heavy_company")).data[0]?.id;
        const r = id ? await api.put(`/admin/c/heavy_company/${id}`, p) : await api.post("/admin/c/heavy_company", p);
        if (pending.current === 1) { setD(r.data); toast.success("Guardado"); }
      } catch (e) { toast.error(errMsg(e)); } finally { pending.current -= 1; }
    });
  };
  if (!d) return null;
  const P = { row: { ...d, id: "c" }, onSave: save, prefix: "hco" };
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 max-w-3xl" data-testid="hco">
      <p className="text-sm text-slate-500">Estos datos salen arriba en las facturas y reportes en PDF.</p>
      <div className="grid gap-4 sm:grid-cols-2"><Txt {...P} k="name" label="Nombre de la empresa" /><Txt {...P} k="rnc" label="RNC" /><Txt {...P} k="phone" label="Teléfono" type="tel" /><Txt {...P} k="email" label="Email" type="email" />
        <div className="sm:col-span-2"><Txt {...P} k="address" label="Dirección" /></div></div>
    </section>
  );
}
